import { DocumentNode, useMutation } from '@apollo/client';
import {
  MostRunCommandType,
  RunMostQueryDocument,
  RunMostTriageDocument,
} from 'src/graphql/generated/graphql';
import { useStores } from './use-stores';

export type Command = {
  device: string | string[];
  nationalStoreNumber: string;
  queryId?: string | null;
  triageId?: string | null;
  name?: string | null;
};

export type MostJobConfig = {
  command: {
    commandId: string;
    description?: string;
    name: string;
    type: MostRunCommandType;
    appliesToBOS: boolean;
    appliesToEPR: boolean;
    appliesToPOS: boolean;
    appliesToKVS: boolean;
    appliesToRHS: boolean;
    appliesToWST: boolean;
  };
  devices: string[];
  storeIds: string[];
};

export const useRunCommand = () => {
  const [runQuery, { loading: loadingQuery }] =
    useMutation(RunMostQueryDocument);
  const [runTriage, { loading: loadingTriage }] = useMutation(
    RunMostTriageDocument,
  );

  const loading = loadingQuery || loadingTriage;

  const { stores } = useStores();

  const runCommand = (
    { device, nationalStoreNumber, queryId, triageId }: Command,
    { refetchQueries }: { refetchQueries?: DocumentNode[] } = {},
  ) => {
    const data = {
      devicesName: Array.isArray(device) ? device : [device],
      nationalStoreNumber,
    };

    if (data.devicesName.length === 0 || !data.nationalStoreNumber) {
      return;
    }

    if (queryId) {
      return runQuery({
        variables: {
          data: {
            id: queryId,
            ...data,
          },
        },
        refetchQueries,
      });
    }

    if (triageId) {
      return runTriage({
        variables: {
          data: {
            id: triageId,
            ...data,
          },
        },
        refetchQueries,
      });
    }
  };

  const submitJobConfig = async (
    jobConfig: MostJobConfig,
    {
      refetchQueries,
      onFinish,
    }: { refetchQueries?: DocumentNode[]; onFinish?: () => void } = {},
  ) => {
    const queryId =
      jobConfig.command.type === MostRunCommandType.Query
        ? jobConfig.command.commandId
        : undefined;
    const triageId =
      jobConfig.command.type === MostRunCommandType.Triage
        ? jobConfig.command.commandId
        : undefined;

    const commands = jobConfig.storeIds
      .map(
        (storeId) =>
          stores.find((store) => store.id === storeId)?.nationalStoreNumber,
      )
      .filter(Boolean)
      .map((nationalStoreNumber) =>
        runCommand(
          {
            nationalStoreNumber: nationalStoreNumber as string,
            device: jobConfig.devices.filter((device) =>
              device.includes(nationalStoreNumber as string),
            ),
            queryId,
            triageId,
          },
          { refetchQueries },
        ),
      );

    await Promise.all(commands);

    onFinish?.();
  };

  return { runCommand, submitJobConfig, loading };
};
